import { AlertTriangle, CheckCircle2, Clock3, Gavel, MinusCircle } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { Badge, StatusBadge, cn } from "@ncp/ui";
import { useCalculationMethod } from "../case/figures";
import type { FilingStatus, ReviewStatus } from "./api";

const FILING_VARIANT: Record<FilingStatus, "danger" | "warning" | "info" | "success" | "outline"> = {
  StandstillBreachCandidate: "danger",
  FailureToNotifyCandidate: "warning",
  NotYetDue: "info",
  Filed: "success",
  OutOfScope: "outline",
};
const FILING_ICON: Record<FilingStatus, typeof Gavel> = { StandstillBreachCandidate: Gavel, FailureToNotifyCandidate: AlertTriangle, NotYetDue: Clock3, Filed: CheckCircle2, OutOfScope: MinusCircle };

/** Filing verdict of the deterministic screen: what the register says about the deal. */
export function FilingBadge({ status, size = "sm" }: { status: FilingStatus; size?: "sm" | "default" }) {
  const { t } = useTranslation();
  const Icon = FILING_ICON[status] ?? MinusCircle;
  return <Badge variant={FILING_VARIANT[status] ?? "outline"} size={size}><Icon /> {t(`backoffice:dealWatch.filing.${status}`, { defaultValue: status })}</Badge>;
}

/** Officer's review state, mapped onto the shared status palette. */
export function ReviewBadge({ status }: { status: ReviewStatus }) {
  const { t } = useTranslation();
  const palette = status === "New" ? "Submitted" : status === "UnderReview" ? "UnderReview" : status === "Escalated" ? "Investigation" : status === "Linked" ? "Accepted" : "Dismissed";
  return <StatusBadge status={palette} label={t(`backoffice:dealWatch.review.${status}`, { defaultValue: status })} />;
}

/**
 * The screening score 0–100 of a deal: the points the screening rules gave it (its factors are listed on the deal). It has a name and a
 * meaning on hover (visual item 12): a lead for review, not a finding.
 */
export function ScoreRing({ score, size = 56 }: { score: number; size?: number }) {
  const { t } = useTranslation();
  const r = 15;
  const c = 2 * Math.PI * r;
  const label = t("backoffice:dealWatch.scoreName", { score: Math.round(score) });
  // The bands of the one risk scale, as the published method holds them (condition 2.16).
  const bands = useCalculationMethod().data?.bands;
  const high = bands?.riskLevelHighFrom ?? 70, medium = bands?.riskLevelMediumFrom ?? 40;
  return (
    <div className="relative grid shrink-0 place-items-center rounded-lg bg-sand-50" style={{ width: size, height: size }} title={`${label}. ${t("backoffice:dealWatch.scoreMeaning")}`} aria-label={label} role="img">
      <svg viewBox="0 0 36 36" className="absolute inset-0 size-full -rotate-90"><circle cx="18" cy="18" r={r} fill="none" className="stroke-sand-200" strokeWidth="3" /><circle cx="18" cy="18" r={r} fill="none" strokeWidth="3" strokeLinecap="round" strokeDasharray={`${(Math.max(0, Math.min(100, score)) / 100) * c} ${c}`} className={score >= high ? "stroke-danger-500" : score >= medium ? "stroke-warning-500" : "stroke-seablue-500"} /></svg>
      <span className={cn("relative font-semibold tabular", size >= 56 ? "text-sm" : "text-xs")}>{Math.round(score)}</span>
    </div>
  );
}

/** One of the three screening verdicts (nexus / control / thresholds) as a compact chip with a tone. */
export function VerdictChip({ group, value }: { group: "nexus" | "control" | "threshold"; value: string }) {
  const { t } = useTranslation();
  const tone = group === "nexus"
    ? value === "None" ? "bg-sand-100 text-sand-700" : "bg-seablue-50 text-seablue-700"
    : group === "control"
      ? value === "Acquired" ? "bg-danger-50 text-danger-700" : value === "Possible" ? "bg-warning-50 text-warning-700" : value === "NotAcquired" ? "bg-success-50 text-success-700" : "bg-sand-100 text-sand-700"
      : value === "Met" ? "bg-danger-50 text-danger-700" : value === "NotMet" ? "bg-success-50 text-success-700" : "bg-sand-100 text-sand-700";
  return <span className={cn("inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium", tone)}><span className="text-[11px] uppercase tracking-wider">{t(`backoffice:dealWatch.verdictGroups.${group}`)}</span> {t(`backoffice:dealWatch.${group}.${value}`, { defaultValue: value })}</span>;
}
